import authService from '../services/auth';
import userService from '../services/user';
import herdService from '../services/herds';
import storageService from '../utils/localStorage';
import posthog from '../utils/posthog';
import { notify } from './notificationReducer';
import { applyThemePreference } from './themeReducer';
import type { ThemePreference } from '../utils/localStorage';
import getErrorMsg from '../utils/getErrorMsg';
import type { AppDispatch } from '../store';
import type { ReduxAction, UserState } from '../types/common';

type Credentials = Record<string, unknown>;

type UserAction = ReduxAction<UserState | Partial<UserState>>;

const userReducer = (state: UserState | null = null, action: UserAction): UserState | null => {
  switch (action.type) {
    case 'LOGIN':
      return action.payload as UserState;
    case 'SIGNUP':
      return action.payload as UserState;
    case 'LOGOUT':
      return null;
    case 'SET_USER':
      return action.payload as UserState;
    case 'SET_JOINED_HERDS':
      return state ? { ...state, joinedHerds: action.payload as unknown as string[] } : state;
    case 'SET_FEED_TOPICS':
      return state ? { ...state, feedTopics: action.payload as unknown as string[] } : state;
    case 'SET_AVATAR':
      return state ? { ...state, ...(action.payload as Partial<UserState>) } : state;
    case 'REMOVE_AVATAR':
      return { ...state, avatar: { exists: false } };
    case 'UPDATE_COSMETICS':
      return state ? { ...state, ...(action.payload as Partial<UserState>) } : state;
    case 'UPDATE_STREAK':
      return state ? { ...state, streak: (action.payload as Partial<UserState>).streak } : state;
    default:
      return state;
  }
};

/**
 * Update the logged-in user's streak in place (and in localStorage) after the
 * activity tracker earns it — keeps the nav/profile streak count in sync without
 * a re-fetch.
 */
export const updateUserStreak = (streak: UserState['streak']) => {
  return (dispatch: AppDispatch) => {
    const stored = storageService.loadUser();
    if (stored) storageService.saveUser({ ...stored, streak });
    dispatch({ type: 'UPDATE_STREAK', payload: { streak } });
  };
};

export const loginUser = (credentials: Credentials) => {
  return async (dispatch: AppDispatch) => {
    const user = await authService.login(credentials);
    storageService.saveUser(user);
    authService.setToken(user.token);

    posthog.identify(user.id, { username: user.username });
    posthog.capture('login_completed', { username: user.username });

    dispatch({
      type: 'LOGIN',
      payload: user,
    });

    // The account's saved theme preference takes over on login.
    if (user.themePreference) {
      dispatch(applyThemePreference(user.themePreference as ThemePreference));
    }
  };
};

export const signupUser = (credentials: Credentials) => {
  return async (dispatch: AppDispatch) => {
    const user = await authService.signup(credentials);
    storageService.saveUser(user);
    authService.setToken(user.token);

    posthog.identify(user.id, { username: user.username });
    posthog.capture('signup_completed', { username: user.username });

    dispatch({
      type: 'SIGNUP',
      payload: user,
    });

    // Apply the new account's theme preference (defaults to 'system').
    if (user.themePreference) {
      dispatch(applyThemePreference(user.themePreference as ThemePreference));
    }
  };
};

export const logoutUser = () => {
  return (dispatch: AppDispatch) => {
    storageService.logoutUser();
    authService.setToken(null);

    posthog.capture('logout');
    posthog.reset();

    dispatch({
      type: 'LOGOUT',
    });
  };
};

export const setUser = () => {
  return (dispatch: AppDispatch) => {
    const loggedUser = storageService.loadUser();

    if (loggedUser) {
      authService.setToken(loggedUser.token);

      dispatch({
        type: 'SET_USER',
        payload: loggedUser,
      });
    }
  };
};

/**
 * Toggle the current user's subscription to a herd. Membership is stored
 * once (here, on the user); the optimistic update also nudges the cached
 * subscriber counts in the herd list / herd page slices and is reverted on error.
 */
export const toggleHerdMembership = (subId: string) => {
  return async (dispatch: AppDispatch, getState: () => any) => {
    const state = getState();
    const user = state.user as UserState | null;
    if (!user) return;

    const current = user.joinedHerds || [];
    const subscribed = current.includes(subId);
    const delta = subscribed ? -1 : 1;
    const newSubs = subscribed ? current.filter((s) => s !== subId) : [...current, subId];

    const onHerdPage = state.herdPage?.herdDetails?.id === subId;
    const baseCount = onHerdPage ? state.herdPage.herdDetails.memberCount || 0 : 0;

    const applyMembership = (herds: string[]) => {
      dispatch({ type: 'SET_JOINED_HERDS', payload: herds });
      const stored = storageService.loadUser();
      if (stored) storageService.saveUser({ ...stored, joinedHerds: herds });
    };

    // Optimistic update
    applyMembership(newSubs);
    dispatch({ type: 'ADJUST_MEMBER_COUNT', payload: { id: subId, delta } });
    if (onHerdPage) dispatch({ type: 'JOIN_HERD', payload: { memberCount: baseCount + delta } });

    try {
      await herdService.joinHerd(subId);
    } catch (err) {
      // Revert on failure
      applyMembership(current);
      dispatch({ type: 'ADJUST_MEMBER_COUNT', payload: { id: subId, delta: -delta } });
      if (onHerdPage) dispatch({ type: 'JOIN_HERD', payload: { memberCount: baseCount } });
      /*
       * The last member leaving is not an error to toast at someone — it means
       * "this would delete the herd", which the caller turns into a decision.
       * Rethrow it quietly so the page can ask.
       */
      if ((err as any)?.response?.data?.code !== 'last_member') {
        dispatch(notify(getErrorMsg(err), 'error'));
      }
      throw err;
    }
  };
};

/** Refresh the user's joined-herd ids from the server (keeps the set fresh across sessions/devices). */
export const fetchMyHerds = () => {
  return async (dispatch: AppDispatch) => {
    const data = await herdService.getMyHerds();
    const ids: string[] = (data?.joinedHerds || []).map((s: any) =>
      typeof s === 'string' ? s : s.id || s._id || String(s)
    );
    dispatch({ type: 'SET_JOINED_HERDS', payload: ids });
    const stored = storageService.loadUser();
    if (stored) storageService.saveUser({ ...stored, joinedHerds: ids });
  };
};

/** Persist the user's feed-topic picks: server first, then store + local session. */
export const saveFeedTopics = (feedTopics: string[]) => {
  return async (dispatch: AppDispatch) => {
    const data = await userService.updateFeedTopics(feedTopics);
    const saved: string[] = data?.feedTopics ?? feedTopics;
    dispatch({ type: 'SET_FEED_TOPICS', payload: saved });
    const stored = storageService.loadUser();
    if (stored) storageService.saveUser({ ...stored, feedTopics: saved });
  };
};

export const setAvatar = (avatarImage: unknown) => {
  return async (dispatch: AppDispatch) => {
    const uploadedAvatar = await userService.uploadAvatar({ avatarImage });
    const prevUserData = storageService.loadUser();
    storageService.saveUser({ ...prevUserData, ...uploadedAvatar });

    dispatch({
      type: 'SET_AVATAR',
      payload: uploadedAvatar,
    });
  };
};

export const deleteAvatar = () => {
  return async (dispatch: AppDispatch) => {
    await userService.removeAvatar();
    const prevUserData = storageService.loadUser();
    storageService.saveUser({ ...prevUserData, avatar: { exists: false } });

    dispatch({
      type: 'REMOVE_AVATAR',
    });
  };
};

export default userReducer;
